import { useState } from 'react';
import { daysLeft } from '../../core/trash';
import type { TrashedMaterial } from '../api';

/**
 * "Recently deleted (N)" — the foot of the room.
 *
 * What this teacher deleted in the last thirty days, or everything the group deleted if they
 * are an admin; the Worker decides which, and this lists what it was given. Hidden when there
 * is nothing in it, so a room nobody has deleted from looks as it always did.
 *
 * Collapsed by default. It is a way back, not something to read every visit. A native
 * `<details>`, so it opens from the keyboard and says whether it is open with no extra code.
 *
 * "Delete for good" asks once, in the row, rather than in a browser dialog. It is the one
 * action in the room that cannot be undone, so it is the one that asks.
 */

export interface RecentlyDeletedProps {
  materials: TrashedMaterial[];
  onRestore: (material: TrashedMaterial) => void;
  onPurge: (material: TrashedMaterial) => void;
  error: string | null;
  busy?: boolean;
  /** Injected in tests so the day count is fixed. */
  now?: () => Date;
}

/** "Goes for good in 3 days", "in 1 day", "today". */
export const goesForGood = (deletedAt: string, now: Date): string => {
  const days = daysLeft(deletedAt, now.toISOString());
  if (days <= 0) return 'Goes for good today';
  return `Goes for good in ${days} ${days === 1 ? 'day' : 'days'}`;
};

export default function RecentlyDeleted({
  materials, onRestore, onPurge, error, busy = false, now = () => new Date(),
}: RecentlyDeletedProps) {
  const [confirming, setConfirming] = useState<string | null>(null);

  if (materials.length === 0 && !error) return null;

  const at = now();

  return (
    <details className="panel collab-recently-deleted">
      <summary>Recently deleted ({materials.length})</summary>

      {error && <p className="error" role="alert">{error}</p>}

      <ul>
        {materials.map((material) => (
          <li key={material.id}>
            <span className="trash-title">{material.title}</span>
            <time className="meta" dateTime={material.restorableUntil}>
              {goesForGood(material.deletedAt, at)}
            </time>
            {confirming === material.id ? (
              <span className="row-actions trash-confirm">
                <span>Delete &ldquo;{material.title}&rdquo; for good? This cannot be undone.</span>
                <button
                  type="button"
                  className="danger"
                  disabled={busy}
                  onClick={() => { setConfirming(null); onPurge(material); }}
                >
                  Delete for good
                </button>
                <button type="button" className="secondary" onClick={() => setConfirming(null)}>
                  Cancel
                </button>
              </span>
            ) : (
              <span className="row-actions">
                <button type="button" className="secondary" disabled={busy} onClick={() => onRestore(material)}>
                  Restore
                </button>
                <button
                  type="button"
                  className="secondary danger"
                  disabled={busy}
                  onClick={() => setConfirming(material.id)}
                >
                  Delete for good
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
